import React from "react";

import {
  Button,
  ExpandablePanel,
  ExpandablePanelBody,
  ExpandablePanelHeader,
  Typography,
  TextField,
  Label,
} from "@king-ict/eupisi-ui/components";
import { DocumentList } from "@king-ict/eupisi-ui/components";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import CriterionDataFetchStatusEnum from "../../../models/Criterion/CriterionDataFetchStatusEnum";
import IntegrationEnum from "../../../models/Integration/IntegrationEnum";
import RequestCriterion from "../../../models/Request/RequestCriterion";
import { useCriterionDocuments } from "../../Request/CriteriaStep/useCriterionDocuments";
import ChildrenOfParentsCriterionData from "../CriterionIntegrationData/ChildrenOfParentsCriterionData";
import PermanentAndTemporaryResidenceData from "../CriterionIntegrationData/PermanentAndTemporaryResidenceData";
import styles from "./RequestDetailsPage.module.scss";

interface Props {
  applicantOib?: string;
  otherParentOib?: string;
  childOib?: string;
  criteria: RequestCriterion[];
  moveToSpecificStep?: (step: number, substep?: number) => void;
  onExpandablePanelClick?: () => void;
}

export default function CriterionDetails({
  applicantOib,
  otherParentOib,
  childOib,
  criteria,
  moveToSpecificStep,
  onExpandablePanelClick,
}: Props) {
  const { t } = useTranslation();

  const { handleDownloadDocument } = useCriterionDocuments();

  const getStatusColor = (status: CriterionDataFetchStatusEnum) => {
    switch (status) {
      case CriterionDataFetchStatusEnum.Success:
        return "success";
      case CriterionDataFetchStatusEnum.Fail:
        return "danger";
      case CriterionDataFetchStatusEnum.Unknown:
        return undefined;
      default:
        return undefined;
    }
  };

  const getIntegrationData = (data: string, integrationId?: number) => {
    switch (integrationId) {
      case IntegrationEnum.MUP1:
      case IntegrationEnum.MUP2:
      case IntegrationEnum.MUP3:
        return <PermanentAndTemporaryResidenceData data={data} />;

      case IntegrationEnum.ParentChild:
        return <ChildrenOfParentsCriterionData data={data} />;

      default:
        return <Row>
          <Col md={12}>
            <Typography>{data}</Typography>
          </Col>
        </Row>;
    }
  };

  const renderCriteria = () => {
    if (criteria && criteria.length > 0) {
      return criteria.map((criterion) => (
        <React.Fragment key={`fragment ${criterion.institutionCriterionId}`}>
          <Row key={criterion.institutionCriterionId}>
            <Col md={6}>
              <Typography variant="base" color="light">
                {criterion.criterionName}
              </Typography>
            </Col>
            <Col md={6}>
              <Typography variant="base" color="light">
                {criterion.integrationName}
              </Typography>
            </Col>
          </Row>
          {criterion.integrationData && criterion.integrationId ? (
            <>
              <Row>
                <Col md={12}>
                  <Typography variant="baseBold">
                    {t("requestReviewStep.criterionSystemInformation", {
                      system: criterion.integrationName,
                    })}
                  </Typography>
                </Col>
              </Row>
              {criterion.integrationData.applicantParentData && (
                <>
                  <Row className={styles.integrationStatusRow}>
                    <Col md={6}>
                      <TextField
                        plaintext
                        bold
                        label={t("criteriaStep.applicantParentIntegration")}
                        value={applicantOib}
                      />
                    </Col>
                    <Col md={6} className={styles.status}>
                      <Label
                        color={getStatusColor(
                          criterion.integrationData.applicantParentData
                            .fetchStatus
                        )}
                      >
                        {criterion.integrationData.applicantParentData
                          .fetchStatus === CriterionDataFetchStatusEnum.Success
                          ? t("criteriaStep.successIntegration")
                          : t("criteriaStep.failIntegration")}
                      </Label>
                    </Col>
                  </Row>
                  {criterion.integrationData.applicantParentData.data &&
                    criterion.integrationData.applicantParentData.fetchStatus &&
                    criterion.integrationData.applicantParentData
                      .fetchStatus === CriterionDataFetchStatusEnum.Success &&
                    getIntegrationData(
                      criterion.integrationData.applicantParentData.data,
                      criterion.integrationId
                    )}
                </>
              )}
              {criterion.integrationData.otherParentData && otherParentOib ? (
                <>
                  <Row
                    className={`${styles.integrationStatusRow} ${styles.integrationOtherParentTitle}`}
                  >
                    <Col md={6}>
                      <TextField
                        plaintext
                        bold
                        label={t("criteriaStep.otherParentIntegration")}
                        value={otherParentOib}
                      />
                    </Col>
                    <Col md={6} className={styles.status}>
                      <Label
                        color={getStatusColor(
                          criterion.integrationData.otherParentData.fetchStatus
                        )}
                      >
                        {criterion.integrationData.otherParentData
                          .fetchStatus === CriterionDataFetchStatusEnum.Success
                          ? t("criteriaStep.successIntegration")
                          : t("criteriaStep.failIntegration")}
                      </Label>
                    </Col>
                  </Row>
                  {criterion.integrationData.otherParentData.data &&
                    criterion.integrationData.otherParentData.fetchStatus &&
                    criterion.integrationData.otherParentData.fetchStatus ===
                      CriterionDataFetchStatusEnum.Success &&
                    criterion.integrationId === IntegrationEnum.ParentChild &&
                    getIntegrationData(
                      criterion.integrationData.otherParentData.data,
                      criterion.integrationId
                    )}
                </>
              ) : null}

              {criterion.integrationData.childData && (
                <>
                  <Row className={styles.integrationStatusRow}>
                    <Col md={6}>
                      <TextField
                        plaintext
                        bold
                        label={t("criteriaStep.childIntegration")}
                        value={childOib}
                      />
                    </Col>
                    <Col md={6} className={styles.status}>
                      <Label
                        color={getStatusColor(
                          criterion.integrationData.childData.fetchStatus
                        )}
                      >
                        {criterion.integrationData.childData.fetchStatus ===
                        CriterionDataFetchStatusEnum.Success
                          ? t("criteriaStep.successIntegration")
                          : t("criteriaStep.failIntegration")}
                      </Label>
                    </Col>
                  </Row>
                  {criterion.integrationData.childData.data &&
                    criterion.integrationData.childData.fetchStatus &&
                    criterion.integrationData.childData.fetchStatus ===
                      CriterionDataFetchStatusEnum.Success &&
                    getIntegrationData(
                      criterion.integrationData.childData.data,
                      criterion.integrationId
                    )}
                </>
              )}
            </>
          ) : null}
          <Typography
            variant="mediumBold"
            color="base"
            className={styles.documentSpaceClass}
          >
            {t("criteriaStep.documentsAdded")}
          </Typography>
          {criterion.documents && criterion.documents.length > 0 ? (
            <div className={styles.criterionDocuments}>
              <DocumentList
                documents={criterion.documents}
                removable={false}
                onDocumentClick={handleDownloadDocument}
              />
            </div>
          ) : (
            <Typography variant="base" color="light">
              {t("criteriaStep.noDocuments")}
            </Typography>
          )}
          <hr className={styles.line} />
        </React.Fragment>
      ));
    }
  };

  return (
    <Row className={styles.rowSpace}>
      <Col md={12}>
        <ExpandablePanel clickableHeader>
          <ExpandablePanelHeader onClick={onExpandablePanelClick}>
            {t("requestReviewStep.criteria")}
          </ExpandablePanelHeader>
          <ExpandablePanelBody>
            <Row className={styles.rowSpace}>
              <Col md={10}></Col>
              {moveToSpecificStep && (
                <Col md={2}>
                  <Button
                    size="sm"
                    outline
                    onClick={() => moveToSpecificStep(3)}
                  >
                    {t("common.edit")}
                  </Button>
                </Col>
              )}
            </Row>
            <Row>
              <Col md={6}>
                <Typography variant="baseBold">
                  {t("requestReviewStep.criterion")}
                </Typography>
              </Col>
              <Col md={6}>
                <Typography variant="baseBold">
                  {t("requestReviewStep.criterionSystem")}
                </Typography>
              </Col>
            </Row>
            {renderCriteria()}
          </ExpandablePanelBody>
        </ExpandablePanel>
      </Col>
    </Row>
  );
}
